import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CalibrationDocumentationModal = ({ isOpen, onClose, equipment, onSave }) => {
  const [formData, setFormData] = useState({
    calibrationDate: new Date()?.toISOString()?.split('T')?.[0],
    nextDueDate: '',
    technician: 'Dr. Sarah Chen',
    method: '',
    standardsUsed: '',
    results: '',
    deviations: '',
    corrective_actions: '',
    certificateNumber: '',
    notes: '',
    attachments: []
  });
  const [signature, setSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const calibrationMethods = [
    'NIST Traceable Standards',
    'ISO 17025 Procedure',
    'Manufacturer Specifications',
    'Internal Standard Operating Procedure',
    'Pharmacopoeia Method'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event?.target?.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...files?.map(file => ({
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadDate: new Date()?.toISOString()
      }))]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter((_, i) => i !== index)
    }));
  };

  const generateCertificateNumber = () => {
    const date = new Date();
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    const random = Math.floor(Math.random() * 1000)?.toString()?.padStart(3, '0');
    return `CAL-${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    try {
      const calibrationRecord = {
        ...formData,
        equipmentId: equipment?.id,
        deviceId: equipment?.deviceId,
        signature,
        blockchainHash: `0x${Math.random()?.toString(16)?.substr(2, 64)}`, // Mock blockchain hash
        timestamp: new Date()?.toISOString(),
        status: 'completed',
        certificateNumber: formData?.certificateNumber || generateCertificateNumber()
      };

      await onSave(calibrationRecord);
      onClose();
    } catch (error) {
      console.error('Error saving calibration record:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-popover border border-border rounded-lg shadow-modal overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-popover-foreground">Calibration Documentation</h2>
              <p className="text-sm text-muted-foreground">
                {equipment?.deviceId} - {equipment?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Calibration Date"
                type="date"
                value={formData?.calibrationDate}
                onChange={(e) => handleInputChange('calibrationDate', e?.target?.value)}
                required
              />
              
              <Input
                label="Next Due Date"
                type="date"
                value={formData?.nextDueDate}
                onChange={(e) => handleInputChange('nextDueDate', e?.target?.value)}
                required
              />
              
              <Input
                label="Technician"
                type="text"
                value={formData?.technician}
                onChange={(e) => handleInputChange('technician', e?.target?.value)}
                required
              />
              
              <Input
                label="Certificate Number"
                type="text"
                value={formData?.certificateNumber}
                onChange={(e) => handleInputChange('certificateNumber', e?.target?.value)}
                placeholder="Auto-generated if empty"
              />
            </div>

            {/* Calibration Method */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Calibration Method *
              </label>
              <select
                value={formData?.method}
                onChange={(e) => handleInputChange('method', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground"
                required
              >
                <option value="">Select calibration method</option>
                {calibrationMethods?.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            {/* Standards Used */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Standards Used *
              </label>
              <textarea
                value={formData?.standardsUsed}
                onChange={(e) => handleInputChange('standardsUsed', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-20 resize-none"
                placeholder="List all reference standards, their certificate numbers, and traceability information"
                required
              />
            </div>

            {/* Results */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Calibration Results *
              </label>
              <textarea
                value={formData?.results}
                onChange={(e) => handleInputChange('results', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-32 resize-none"
                placeholder={`Record measurement results, accuracy, precision, and any statistical analysis\n\nExample:\n- Accuracy: ±0.1% at 25°C\n- Precision: RSD < 0.05%\n- Linearity: R² > 0.999\n- Range: 0-100% of scale`}
                required
              />
            </div>

            {/* Deviations and Corrective Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-popover-foreground mb-2">
                  Deviations Observed
                </label>
                <textarea
                  value={formData?.deviations}
                  onChange={(e) => handleInputChange('deviations', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-24 resize-none"
                  placeholder="Document any deviations from expected results or procedures"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-popover-foreground mb-2">
                  Corrective Actions
                </label>
                <textarea
                  value={formData?.corrective_actions}
                  onChange={(e) => handleInputChange('corrective_actions', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-24 resize-none"
                  placeholder="Describe any corrective actions taken to address deviations"
                />
              </div>
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Supporting Documents
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6">
                <div className="text-center">
                  <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Upload calibration certificates, raw data, or photos
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef?.current?.click()}
                    iconName="Plus"
                    iconSize={16}
                  >
                    Add Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                
                {formData?.attachments?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {formData?.attachments?.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <div className="flex items-center space-x-2">
                          <Icon name="File" size={16} className="text-muted-foreground" />
                          <span className="text-sm text-foreground">{file?.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(file?.size)})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-error hover:text-error/80 transition-colors duration-150"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Digital Signature */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Digital Signature *
              </label>
              <div className="border border-border rounded-md p-4 bg-muted/20">
                <div className="flex items-center space-x-3 mb-3">
                  <Icon name="PenTool" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Electronic Signature</span>
                </div>
                <Input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e?.target?.value)}
                  placeholder="Type your full name to sign this document"
                  required
                />
                <p className="text-xs text-muted-foreground mt-2">
                  By typing your name, you certify that the information provided is accurate and complete.
                  This signature will be recorded on the blockchain for immutable verification.
                </p>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-popover-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground h-20 resize-none"
                placeholder="Any additional observations, recommendations, or comments"
              />
            </div>

            {/* Blockchain Notice */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={20} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Blockchain Verification</p>
                  <p className="text-xs text-muted-foreground">
                    This calibration record will be stored on the blockchain for immutable audit trail and regulatory compliance.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            iconName="Save"
            iconSize={16}
          >
            Save & Submit to Blockchain
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalibrationDocumentationModal;